import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SuiviConservation } from "../components/artwork/SuiviConservation";
import { supabase } from "../lib/supabaseClient";

// Fiches de conservation génériques par type d'œuvre
const CONSERVATION_TEMPLATES = {
  photographie: {
    temperature: "18-20°C",
    humidity: "30-45% RH",
    light: "Maximum 50 lux, sans exposition directe aux UV",
    placement: "Éviter les murs extérieurs et sources de chaleur",
    rotation: "Rotation recommandée tous les 3 mois",
    additionalRecommendations: [
      "Vérifier régulièrement la présence de poussière ou de décolorations",
      "Utiliser des gants en coton lors de toute manipulation",
      "Maintenir dans un environnement sans poussière avec filtration d'air",
      "En cas de déplacement, attendre 24h d'acclimatation avant de déballer",
      "Éviter les variations brusques de température et d'humidité",
      "Conserver dans des boîtes acid-free ou des pochettes de polyester",
      "Protection UV obligatoire (verre ou plexiglas filtrant)",
      "Éviter tout contact direct avec adhésifs ou matériaux acides"
    ]
  },
  peinture: {
    temperature: "18-22°C",
    humidity: "45-60% RH",
    light: "Maximum 150 lux, sans exposition directe aux UV",
    placement: "Éviter les murs extérieurs et sources de chaleur",
    rotation: "Rotation recommandée tous les 6 mois",
    additionalRecommendations: [
      "Vérifier régulièrement la présence de fissures, d'écaillage ou de décoloration",
      "Utiliser des gants en coton lors de toute manipulation",
      "Inspecter l'état du support (toile, panneau) et du châssis",
      "En cas de déplacement, attendre 24h d'acclimatation avant de déballer",
      "Éviter les variations brusques de température et d'humidité",
      "Surveiller les signes d'infestation d'insectes",
      "Maintenir une distance de sécurité avec les murs",
      "Éviter les vibrations et chocs",
      "Contrôler régulièrement l'état du vernis de protection",
      "Documenter photographiquement tout changement d'état"
    ]
  },
  sculpture: {
    temperature: "18-22°C (variable selon matériaux)",
    humidity: "45-60% RH (ajuster selon matériaux)",
    light: "Maximum 200 lux, protection UV recommandée",
    placement: "Surface stable, éviter les zones de passage",
    rotation: "Inspection recommandée tous les 6 mois",
    additionalRecommendations: [
      "Adapter les conditions selon les matériaux spécifiques (métal, pierre, bois, etc.)",
      "Vérifier la stabilité de la base et des fixations",
      "Utiliser des gants appropriés selon le matériau lors de toute manipulation",
      "Contrôler les signes de corrosion, fissuration ou détérioration",
      "En cas de déplacement, utiliser un équipement de manutention adapté",
      "Éviter les variations brusques de température et d'humidité",
      "Surveiller les signes d'infestation pour les matériaux organiques",
      "Maintenir un périmètre de sécurité autour de l'œuvre",
      "Documenter l'état de surface et les éventuelles altérations",
      "Prévoir un support ou socle adapté au poids et à la forme",
      "Vérifier régulièrement l'équilibre et la stabilité"
    ]
  },
  art_interactif: {
    temperature: "18-22°C",
    humidity: "45-55% RH",
    light: "Selon spécifications techniques de l'œuvre",
    placement: "Zone stable électriquement, loin des champs magnétiques",
    rotation: "Test de fonctionnement recommandé tous les 3 mois",
    additionalRecommendations: [
      "Documenter et archiver tous les composants techniques (logiciels, hardware)",
      "Maintenir une copie de sauvegarde des fichiers numériques",
      "Vérifier régulièrement la compatibilité des formats de fichiers",
      "Tester périodiquement tous les éléments interactifs",
      "Prévoir un plan de migration technologique",
      "Conserver la documentation technique originale",
      "Effectuer des sauvegardes sur supports multiples",
      "Surveiller l'obsolescence des technologies utilisées",
      "Maintenir un inventaire détaillé de tous les composants",
      "Prévoir des pièces de rechange pour les éléments critiques"
    ]
  },
  generale: {
    temperature: "18-20°C",
    humidity: "45-55%",
    light: "Maximum 150 lux, sans exposition directe aux UV",
    placement: "Éviter les murs extérieurs et sources de chaleur",
    rotation: "Rotation recommandée tous les 6 mois",
    additionalRecommendations: [
      "Vérifier régulièrement la présence de poussière ou de décolorations",
      "Utiliser des gants en coton lors de toute manipulation",
      "En cas de déplacement, attendre 24h d'acclimatation avant de déballer",
      "Éviter les variations brusques de température et d'humidité"
    ]
  }
};
// Helper: génération PDF via Edge Function
async function generateConservationPdf(artworkId) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate_conservation_pdf?artwork_id=${encodeURIComponent(artworkId)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${session?.access_token ?? ""}`,
        "Content-Type": "application/json",
      },
    }
  );
  if (!res.ok) throw new Error(`PDF generation failed: ${res.status}`);
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Conservation_${artworkId}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function localizeTemplate(type, t) {
  const raw = CONSERVATION_TEMPLATES[type] || CONSERVATION_TEMPLATES.generale;
  return {
    ...raw,
    light: t(`conservationPage.templates.${type}.light`, { defaultValue: raw.light }),
    placement: t(`conservationPage.templates.${type}.placement`, { defaultValue: raw.placement }),
    rotation: t(`conservationPage.templates.${type}.rotation`, { defaultValue: raw.rotation }),
    additionalRecommendations: raw.additionalRecommendations.map((value, index) =>
      t(`conservationPage.templates.${type}.recommendations.${index}`, { defaultValue: value })
    ),
  };
}

export const Conservation = () => {
  const { artworkId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const language = String(i18n.resolvedLanguage || i18n.language || "fr").toLowerCase();
  const locale = language.startsWith("de") ? "de-DE" : language.startsWith("en") ? "en-US" : "fr-FR";

  const id = artworkId;
  const [activeTab, setActiveTab] = useState("storage");
  const [showQrModal, setShowQrModal] = useState(false);
  const [artworkType, setArtworkType] = useState("generale");
  const [hasSpecificInstructions, setHasSpecificInstructions] = useState(false);
  const [customInstructions, setCustomInstructions] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [conservationReport, setConservationReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(true);

  // ── Historique maintenance ──────────────────────────────────────
  const [historyRecords, setHistoryRecords] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [newEntry, setNewEntry] = useState({ type: "Inspection", date: "", contact: "", notes: "", cost: "" });
  const [savingEntry, setSavingEntry] = useState(false);

  const ENTRY_TYPES = ["Acquisition", "Inspection", "Restauration", "Nettoyage", "Encadrement", "Transport", "Accrochage", "Décrochage", "Traitement préventif", "Autre"];
  const entryTypeLabel = (key) => t(`conservationPage.history.entryTypes.${key}`, key);

  const fetchHistory = async () => {
    if (!artworkId) return;
    setHistoryLoading(true);
    const { data } = await supabase
      .from("maintenance")
      .select("id, date, type, contact, notes, cost")
      .eq("artwork_id", artworkId)
      .order("date", { ascending: false });
    setHistoryRecords(data ?? []);
    setHistoryLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === "history") fetchHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, artworkId]);

  const handleAddEntry = async () => {
    if (!newEntry.date || !newEntry.type) return;
    setSavingEntry(true);
    const { error } = await supabase.from("maintenance").insert({
      artwork_id: artworkId,
      date: newEntry.date,
      type: newEntry.type,
      contact: newEntry.contact || null,
      notes: newEntry.notes || null,
      cost: newEntry.cost ? parseFloat(newEntry.cost) : null,
    });
    if (!error) {
      setNewEntry({ type: "Inspection", date: "", contact: "", notes: "", cost: "" });
      setShowAddEntry(false);
      fetchHistory();
    }
    setSavingEntry(false);
  };
  // Fonction pour détecter le type d'œuvre à partir des données
  const detectArtworkType = (artworkData) => {
    if (!artworkData) return "generale";

    const medium = (artworkData.medium || "").toLowerCase();
    const typeOfArt = (artworkData.typeOfArt || "").toLowerCase();
    const title = (artworkData.title || "").toLowerCase();
    const artist = (artworkData.artist || "").toLowerCase();

    // Mots-clés pour chaque type
    const keywords = {
      photographie: [
        'photographie', 'photo', 'tirage', 'épreuve', 'argentique', 'numérique',
        'gélatino-argentique', 'chromogenic', 'c-print', 'lambda', 'inkjet',
        'pigment', 'daguerréotype', 'cyanotype', 'polaroid'
      ],
      peinture: [
        'peinture', 'huile', 'toile', 'acrylique', 'aquarelle', 'gouache',
        'tempera', 'fresque', 'pastel', 'craie', 'pigment', 'tableau',
        'canvas', 'panneau', 'bois'
      ],
      sculpture: [
        'sculpture', 'bronze', 'marbre', 'pierre', 'bois', 'métal',
        'céramique', 'plâtre', 'terre', 'argile', 'fonte', 'assemblage',
        'installation', 'volume', 'relief', 'statue', 'bust'
      ],
      art_interactif: [
        'numérique', 'digital', 'interactif', 'vidéo', 'projection',
        'écran', 'ordinateur', 'capteur', 'nft', 'blockchain',
        'réalité virtuelle', 'vr', 'ar', 'intelligence artificielle',
        'ia', 'générative', 'algorithmique', 'media art'
      ]
    };

    // Texte combiné pour la recherche
    const searchText = `${medium} ${typeOfArt} ${title} ${artist}`.toLowerCase();

    // Vérifier les correspondances pour chaque type
    for (const [type, typeKeywords] of Object.entries(keywords)) {
      const matches = typeKeywords.filter(keyword => 
        searchText.includes(keyword)
      ).length;
      
      if (matches > 0) {
        return type;
      }
    }

    return "generale";
  };

  // Récupération des données de l'œuvre et détection du type
  useEffect(() => {
    const fetchArtworkData = async () => {
      if (!artworkId) return;

      try {
        const { data, error } = await supabase
          .from("artworks")
          .select("title, artist, medium, typeOfArt, condition, conditionNotes")
          .eq("id", artworkId)
          .single();

        if (error) {
          console.error("Erreur lors de la récupération de l'œuvre:", error);
          setArtworkType("generale");
          return;
        }

        if (data) {
          const detectedType = detectArtworkType(data);
          setArtworkType(detectedType);
          
          // Vérifier s'il y a des instructions de conservation spécifiques
          // Vous pouvez ajouter une colonne 'conservation_instructions' dans votre table
          if (data.conservation_instructions) {
            setHasSpecificInstructions(true);
            setCustomInstructions(JSON.parse(data.conservation_instructions));
          }
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);
        setArtworkType("generale");
      }
    };

    fetchArtworkData();
  }, [artworkId]);

  useEffect(() => {
    const fetchConservationReport = async () => {
      if (!artworkId) return;
      setReportLoading(true);
      const { data, error } = await supabase
        .from("conservation_reports")
        .select("*")
        .eq("artwork_id", artworkId)
        .order("created_at", { ascending: false })
        .limit(1);

      if (!error && data && data.length > 0) {
        setConservationReport(data[0]);
      } else {
        setConservationReport(null);
      }
      setReportLoading(false);
    };

    fetchConservationReport();
  }, [artworkId]);

  const handleBackClick = () => {
    if (id && id !== 'undefined') {
      navigate(`/artwork/${id}`);
    } else {
      navigate('/collection');
    }
  };


  const handleRemoveSpecificInstructions = async () => {
    try {
      const { error } = await supabase
        .from("artworks")
        .update({ 
          conservation_instructions: null,
          conservation_instructions_source: null,
          conservation_instructions_date: null
        })
        .eq("id", artworkId);

      if (error) throw error;

      setCustomInstructions(null);
      setHasSpecificInstructions(false);
      alert(t("conservationPage.storage.removed"));
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      alert(t("conservationPage.storage.removeError"));
    }
  };

  // Obtenir les données de conservation selon le type
  const getConservationData = () => {
    if (hasSpecificInstructions && customInstructions) {
      return customInstructions;
    }
    return localizeTemplate(artworkType, t);
  };

  const storageData = getConservationData();

  // Données simulées pour les recommandations de transport (inchangées)
  const transportData = {
    caseType: t("conservationPage.transport.values.caseType"),
    caseSize: "120 x 170 x 20 cm",
    packagingMethod: t("conservationPage.transport.values.packagingMethod"),
    transportMode: t("conservationPage.transport.values.transportMode"),
    carrier: "ArtTransit International",
    estimatedCost: "1 200 € - 1 800 €",
    insurance: t("conservationPage.transport.values.insurance"),
    customs: t("conservationPage.transport.values.customs"),
    temperatureControl: t("conservationPage.transport.values.temperatureControl"),
  };
  const transportFieldLabels = Object.fromEntries(Object.keys(transportData).map(key => [key, t(`conservationPage.transport.fields.${key}`)]));

  const getArtworkTypeLabel = (type) => t(`conservationPage.types.${type || "generale"}`, { defaultValue: t("conservationPage.types.generale") });

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="flex items-center mb-8">
        <button 
          onClick={handleBackClick}
          className="text-gray-dark hover:text-black mr-4 flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="font-serif text-3xl">{t("conservationPage.title")}</h1>
      </div>

      <div className="border-b border-gray-medium mb-8">
        <div className="flex text-sm">
          <button 
            className={`py-3 px-6 font-medium border-b-2 ${activeTab === "monitoring" ? "border-black" : "border-transparent text-gray-dark"}`}
            onClick={() => setActiveTab("monitoring")}
          >
            {t("conservationPage.tabs.monitoring")}
          </button>
          <button 
            className={`py-3 px-6 font-medium border-b-2 ${activeTab === "storage" ? "border-black" : "border-transparent text-gray-dark"}`}
            onClick={() => setActiveTab("storage")}
          >
            {t("conservationPage.tabs.idealStorage")}
          </button>
          <button 
            className={`py-3 px-6 font-medium border-b-2 ${activeTab === "transport" ? "border-black" : "border-transparent text-gray-dark"}`}
            onClick={() => setActiveTab("transport")}
          >
            {t("conservationPage.tabs.transport")}
          </button>
          <button 
            className={`py-3 px-6 font-medium border-b-2 ${activeTab === "history" ? "border-black" : "border-transparent text-gray-dark"}`}
            onClick={() => setActiveTab("history")}
          >
            {t("conservationPage.tabs.history")}
          </button>
        </div>
      </div>

      {activeTab === "storage" && (
        <div>
          <div className="mb-8">
            <div className="flex items-start mb-4">
              <div className="bg-indigo-100 p-3 rounded-full mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="font-serif text-lg">{t("conservationPage.storage.title")}</h2>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      hasSpecificInstructions 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {hasSpecificInstructions ? t("conservationPage.storage.specificBadge") : t("conservationPage.storage.genericBadge", { type: getArtworkTypeLabel(artworkType) })}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-dark">
                  {hasSpecificInstructions ? t("conservationPage.storage.specificBody") : t("conservationPage.storage.genericBody")}
                </p>
                {!hasSpecificInstructions && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-start gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-xs text-blue-800">
                        <strong>{t("conservationPage.storage.genericTitle")}</strong> {t("conservationPage.storage.genericNotice")}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b">
                    <td className="py-3 px-4 font-medium">{t("conservationPage.storage.temperature")}</td>
                    <td className="py-3 px-4 text-gray-dark">{storageData.temperature}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 px-4 font-medium">{t("conservationPage.storage.humidity")}</td>
                    <td className="py-3 px-4 text-gray-dark">{storageData.humidity}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 px-4 font-medium">{t("conservationPage.storage.light")}</td>
                    <td className="py-3 px-4 text-gray-dark">{storageData.light}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 px-4 font-medium">{t("conservationPage.storage.placement")}</td>
                    <td className="py-3 px-4 text-gray-dark">{storageData.placement}</td>
                  </tr>
                  <tr className="border-b-0">
                    <td className="py-3 px-4 font-medium">{t("conservationPage.storage.rotation")}</td>
                    <td className="py-3 px-4 text-gray-dark">{storageData.rotation}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-gray-light p-6 rounded-md">
            <h3 className="font-medium mb-4">{t("conservationPage.storage.additional", { type: getArtworkTypeLabel(artworkType) })}</h3>
            <ul className="list-disc pl-5 space-y-2 text-sm text-gray-dark">
              {storageData.additionalRecommendations.map((recommendation, index) => (
                <li key={index}>{recommendation}</li>
              ))}
            </ul>
          </div>

          {/* Section pour gérer les instructions spécifiques */}
          <div className="mt-8 p-6 border-2 border-dashed border-gray-300 rounded-md">
            <div className="text-center">
              {hasSpecificInstructions ? (
                // Interface pour les instructions existantes
                <div>
                  <div className="mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-green-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 className="font-medium text-gray-900 mb-2">{t("conservationPage.storage.specificActiveTitle")}</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    {t("conservationPage.storage.specificActiveBody")}
                  </p>
                  <div className="flex justify-center gap-3">
                    <button 
                      onClick={() => {
                        // Ouvrir un modal pour modifier les instructions
                        // setShowEditInstructionsModal(true);
                        alert(t("conservationPage.storage.comingSoon"));
                      }}
                      className="btn-artsy btn-secondary-artsy"
                    >
                      {t("conservationPage.storage.edit")}
                    </button>
                    <button 
                      onClick={handleRemoveSpecificInstructions}
                      className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm"
                    >
                      {t("conservationPage.storage.backGeneric")}
                    </button>
                  </div>
                </div>
              ) : (
                // Interface pour ajouter des instructions
                <div>
                  <div className="mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <h4 className="font-medium text-gray-900 mb-2">{t("conservationPage.storage.specificAvailableTitle")}</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    {t("conservationPage.storage.specificAvailableBody")}
                  </p>
                  <button 
                    onClick={() => {
                      // Ouvrir un modal pour saisir les instructions
                      // setShowAddInstructionsModal(true);
                      alert(t("conservationPage.storage.comingSoon"));
                    }}
                    className="btn-artsy btn-secondary-artsy"
                  >
                    {t("conservationPage.storage.addSpecific")}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Note importante */}
          <div className="mt-6 bg-amber-50 border border-amber-200 p-4 rounded-md">
            <div className="flex items-start gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div className="text-sm">
                <p className="font-medium text-amber-800 mb-1">{t("conservationPage.storage.importantTitle")}</p>
                <p className="text-amber-700">
                  {t("conservationPage.storage.importantBody")}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "monitoring" && (
        <div>
          {reportLoading ? (
            <p className="text-sm text-gray-500 italic">{t("conservationPage.monitoring.loading")}</p>
          ) : conservationReport ? (
            <>
              <SuiviConservation
                data={{
                  lastFullReport: conservationReport.last_full_report
                    ? new Date(conservationReport.last_full_report).toLocaleDateString(locale, { month: "2-digit", year: "numeric" })
                    : "—",
                  lastInspection: conservationReport.last_inspection
                    ? new Date(conservationReport.last_inspection).toLocaleDateString(locale, { month: "2-digit", year: "numeric" })
                    : "—",
                  lastTransport: conservationReport.last_transport
                    ? new Date(conservationReport.last_transport).toLocaleDateString(locale, { month: "2-digit", year: "numeric" })
                    : "—",
                  riskLevel: conservationReport.risk_level || "—",
                  nextInspectionDue: conservationReport.next_inspection_due
                    ? new Date(conservationReport.next_inspection_due).toLocaleDateString(locale, { month: "2-digit", year: "numeric" })
                    : "—",
                  nextFullReportDue: conservationReport.next_full_report_due
                    ? new Date(conservationReport.next_full_report_due).toLocaleDateString(locale, { month: "2-digit", year: "numeric" })
                    : "—",
                  recommendation: conservationReport.recommendation || "—",
                }}
              />

              {/* Badge alerte si échéances dépassées */}
              {(() => {
                const today = new Date();
                const inspectionLate = conservationReport.next_inspection_due && new Date(conservationReport.next_inspection_due) < today;
                const reportLate = conservationReport.next_full_report_due && new Date(conservationReport.next_full_report_due) < today;
                if (!inspectionLate && !reportLate) return null;
                return (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-red-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div className="text-sm text-red-700">
                      <span className="font-medium">{t("conservationPage.monitoring.overdue")} </span>
                      {[inspectionLate && t("conservationPage.monitoring.visualInspection"), reportLate && t("conservationPage.monitoring.fullReport")].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                );
              })()}
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm font-medium">{t("conservationPage.monitoring.emptyTitle")}</p>
              <p className="text-xs mt-1">{t("conservationPage.monitoring.emptyBody")}</p>
            </div>
          )}

          <div className="mt-6 flex justify-end">
<Link
  to={`/conservation-report/${id}`}
  className="btn-artsy btn-primary-artsy"
>
  {conservationReport ? t("conservationPage.monitoring.update") : t("conservationPage.monitoring.create")}
</Link>
          </div>
        </div>
      )}

      {activeTab === "transport" && (
        <div>
          <div className="mb-8">
            <div className="flex items-start mb-4">
              <div className="bg-indigo-100 p-3 rounded-full mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                </svg>
              </div>
              <div>
                <h2 className="font-serif text-lg mb-2">{t("conservationPage.transport.title")}</h2>
                <p className="text-sm text-gray-dark">
                  {t("conservationPage.transport.body")}
                </p>
              </div>
            </div>

            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(transportData).map(([key, value]) => (
                    <tr key={key} className="border-b last:border-b-0">
                      <td className="py-3 px-4 font-medium capitalize">{transportFieldLabels[key] || key}</td>
                      <td className="py-3 px-4 text-gray-dark">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="mt-8 flex justify-between">
           <button
              className="btn-artsy btn-secondary-artsy"
              onClick={() => navigate(`/transport/${id}`)}
              disabled={!id}
              title={!id ? t("conservationPage.transport.missingId") : t("conservationPage.transport.openForm")}
            >
              {t("conservationPage.transport.requestQuote")}
            </button>
            <div className="flex space-x-3">
              <button 
                onClick={() => setShowQrModal(true)} 
                className="btn-artsy btn-secondary-artsy"
              >
                {t("conservationPage.transport.generateQr")}
              </button>
              <button
                className="btn-artsy btn-primary-artsy"
                disabled={!id || isGenerating}
                title={!id ? t("conservationPage.transport.missingId") : t("conservationPage.transport.downloadPdf")}
                onClick={async () => {
                  if (!id) return;
                  try {
                    setIsGenerating(true);
                    await generateConservationPdf(id);
                  } catch (e) {
                    console.error(e);
                    alert(t("conservationPage.transport.pdfError"));
                  } finally {
                    setIsGenerating(false);
                  }
                }}
              >
                {isGenerating ? t("conservationPage.transport.generating") : t("conservationPage.transport.generatePdf")}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "history" && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-gray-dark">
              {t("conservationPage.history.subtitle")}
            </p>
            <button
              onClick={() => setShowAddEntry(v => !v)}
              className="text-sm border border-gray-300 px-3 py-1.5 hover:bg-gray-50"
            >
              {t("conservationPage.history.addEntry")}
            </button>
          </div>

          {/* Formulaire d'ajout */}
          {showAddEntry && (
            <div className="mb-8 p-4 border border-gray-200 bg-gray-50">
              <h4 className="text-sm font-medium mb-4">{t("conservationPage.history.form.title")}</h4>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">{t("conservationPage.history.form.type")}</label>
                  <select
                    value={newEntry.type}
                    onChange={e => setNewEntry(v => ({ ...v, type: e.target.value }))}
                    className="w-full border border-gray-300 px-2 py-1.5 text-sm"
                  >
                    {ENTRY_TYPES.map(type => <option key={type} value={type}>{entryTypeLabel(type)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">{t("conservationPage.history.form.date")}</label>
                  <input
                    type="date"
                    value={newEntry.date}
                    onChange={e => setNewEntry(v => ({ ...v, date: e.target.value }))}
                    className="w-full border border-gray-300 px-2 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">{t("conservationPage.history.form.contact")}</label>
                  <input
                    type="text"
                    value={newEntry.contact}
                    onChange={e => setNewEntry(v => ({ ...v, contact: e.target.value }))}
                    placeholder={t("conservationPage.history.form.contactPlaceholder")}
                    className="w-full border border-gray-300 px-2 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">{t("conservationPage.history.form.cost")}</label>
                  <input
                    type="number"
                    value={newEntry.cost}
                    onChange={e => setNewEntry(v => ({ ...v, cost: e.target.value }))}
                    placeholder="0"
                    className="w-full border border-gray-300 px-2 py-1.5 text-sm"
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="text-xs text-gray-500 block mb-1">{t("conservationPage.history.form.notes")}</label>
                <textarea
                  value={newEntry.notes}
                  onChange={e => setNewEntry(v => ({ ...v, notes: e.target.value }))}
                  rows={2}
                  placeholder={t("conservationPage.history.form.notesPlaceholder")}
                  className="w-full border border-gray-300 px-2 py-1.5 text-sm resize-none"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowAddEntry(false)}
                  className="text-sm px-3 py-1.5 border border-gray-300"
                >
                  {t("conservationPage.history.form.cancel")}
                </button>
                <button
                  onClick={handleAddEntry}
                  disabled={savingEntry || !newEntry.date}
                  className="text-sm px-3 py-1.5 bg-black text-white disabled:opacity-50"
                >
                  {savingEntry ? t("conservationPage.history.form.saving") : t("conservationPage.history.form.save")}
                </button>
              </div>
            </div>
          )}

          {/* Timeline */}
          {historyLoading ? (
            <p className="text-sm text-gray-dark">{t("conservationPage.history.loading")}</p>
          ) : historyRecords.length === 0 ? (
            <p className="text-sm text-gray-dark italic">{t("conservationPage.history.empty")}</p>
          ) : (
            <div className="border-l-2 border-gray-medium pl-6 space-y-8">
              {historyRecords.map((record) => (
                <div key={record.id} className="relative">
                  <div className="absolute -left-8 top-0 w-4 h-4 rounded-full bg-black"></div>
                  <div>
                    <p className="text-xs text-gray-dark">
                      {record.date ? new Date(record.date).toLocaleDateString(locale) : "—"}
                    </p>
                    <h3 className="text-md font-medium mt-1 mb-1">{entryTypeLabel(record.type)}</h3>
                    {record.contact && (
                      <p className="text-sm text-[#C9A961]">{t("conservationPage.history.intervenant")} : {record.contact}</p>
                    )}
                    {record.notes && (
                      <p className="text-sm text-gray-dark mt-1">{record.notes}</p>
                    )}
                    {record.cost != null && (
                      <p className="text-xs text-gray-400 mt-1">{t("conservationPage.history.cost")} : {Number(record.cost).toLocaleString(locale)} €</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal QR code */}
      {showQrModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-md max-w-md w-full">
            <div className="flex justify-between items-start mb-6">
              <h3 className="font-serif text-xl">{t("conservationPage.qr.title")}</h3>
              <button onClick={() => setShowQrModal(false)} className="text-gray-500 hover:text-black">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex flex-col items-center mb-6">
              <div className="border p-4 mb-4">
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://artguid.com/conservation/${id || 'unknown'}`} 
                  alt={t("conservationPage.qr.alt")} 
                  className="w-48 h-48"
                />
              </div>
              <p className="text-sm text-gray-dark text-center mb-4">
                {t("conservationPage.qr.description")}
              </p>
              <div className="flex space-x-2 text-sm">
                <div className="bg-green-100 text-green-800 px-2 py-1 rounded">{t("conservationPage.qr.secure")}</div>
                <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded">{t("conservationPage.qr.quickAccess")}</div>
              </div>
            </div>
            
            <div className="flex justify-between">
              <button 
                onClick={() => setShowQrModal(false)}
                className="btn-artsy btn-secondary-artsy"
              >
                {t("conservationPage.qr.close")}
              </button>
              <button className="btn-artsy btn-primary-artsy">
                {t("conservationPage.qr.download")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};